import React, { useState, useEffect, useRef } from 'react';
import { Button, Paper, TextField, Typography, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../../api/axios';
import { RootState } from '../../../store';
import SecurityActionModal from './SecurityActionModal';
import AccountDeleteModal from './modals/AccountDeleteModal';
import {logout} from '../../../store/slices/authSlice';
import {useNavigate} from 'react-router-dom';

type TotpDeviceSetupResponseDTO = {
  deviceId: number;
  secret: string;
  otpAuthUrl: string;
  qrCodeBase64: string;
};

const Security: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state: RootState) => state.auth.userId);

  const [currentEmail, setCurrentEmail] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailStep, setEmailStep] = useState<'request' | 'confirm'>('request');
  const [emailCode, setEmailCode] = useState('');
  const [emailTimer, setEmailTimer] = useState(120);
  const emailTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState<TotpDeviceSetupResponseDTO | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [verifyingMfa, setVerifyingMfa] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  const [modalMode, setModalMode] = useState<'add-mfa' | 'remove-mfa' | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  useEffect(() => {
    // Fetch initial security status and user details
    async function fetchStatus() {
      try {
        const mfaResp = await api.get<boolean>(`user/${userId}/mfa-status`);
        setMfaEnabled(mfaResp.data);
        const emailResp = await api.get<{ email: string }>(`user/fetch-user-details`);
        setCurrentEmail(emailResp.data.email);
      } catch (error) { /* Silent */ }
    }
    fetchStatus();
  }, [userId]);

  useEffect(() => {
    // Manage email verification countdown timer
    if (emailStep === 'confirm' && emailTimer > 0) {
      emailTimerRef.current = setTimeout(() => setEmailTimer(prev => prev - 1), 1000);
    }
    if (emailTimer === 0 && emailStep === 'confirm') {
      setEmailStep('request');
      setEmailCode('');
      toast.error(t('profile.security.email.codeExpired'));
    }
    return () => { if (emailTimerRef.current) clearTimeout(emailTimerRef.current); };
  }, [emailStep, emailTimer, t]);

  const handleRequestEmailChange = async () => {
    if (!newEmail.trim()) return;
    try {
      await api.post(`/user/request-email-verification`, { email: newEmail, changeRequest: true });
      toast.success(t('profile.security.email.requestSent'));
      setEmailStep('confirm');
      setEmailTimer(120);
    } catch { toast.error(t('profile.security.email.requestError')); }
  };

  const handleConfirmEmailChange = async () => {
    if (emailCode.trim().length !== 6) return;
    try {
      await api.post(`user/confirm-email-change`, { userId, newEmail, code: emailCode });
      toast.success(t('profile.security.email.success'));
      setCurrentEmail(newEmail);
      setNewEmail('');
      setEmailCode('');
      setEmailStep('request');
    } catch { toast.error(t('profile.security.email.confirmError')); }
  };

  useEffect(() => {
    // Initialize MFA setup when modal opens
    if (modalMode === 'add-mfa') {
      setActiveStep(0);
      const loadMfa = async () => {
        try {
          const response = await api.post<TotpDeviceSetupResponseDTO>(`user/mfa-setup`, { userId });
          setMfaSetupData(response.data);
        } catch {
          toast.error(t('profile.security.mfa.setupError'));
          setModalMode(null);
        }
      };
      loadMfa();
    }

    if (modalMode === null) {
      setMfaCode('');
      setMfaSetupData(null);
      setActiveStep(0);
    }
  }, [modalMode, t, userId]);

  const handleMfaSubmit = async () => {
    setVerifyingMfa(true);
    try {
      if (modalMode === 'add-mfa') {
        await api.post(`user/mfa-verify`, { userId, deviceId: mfaSetupData?.deviceId, totpCode: mfaCode });
        toast.success(t('profile.security.mfa.verified'));
        setMfaEnabled(true);
      } else {
        await api.delete(`user/mfa-remove`, { data: { userId, totpCode: mfaCode } });
        toast.success(t('profile.security.mfa.removed'));
        setMfaEnabled(false);
      }
      setModalMode(null);
    } catch {
      toast.error(modalMode === 'add-mfa' ? t('profile.security.mfa.verifyError') : t('profile.security.mfa.removeError'));
    } finally {
      setVerifyingMfa(false);
    }
  };

  const handleAccountDeleted = () => {
    dispatch(logout());
    navigate('/');
  };

  const emailCodeValid = emailCode.trim().length === 6;
  const mfaCodeValid = mfaCode.trim().length === 6;

  return (
      <Paper className="p-8 max-w-2xl mx-auto flex flex-col space-y-8 bg-white dark:bg-neutral-900 border border-gray-100 dark:border-neutral-800 shadow-2xl rounded-2xl transition-all duration-300">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('profile.security.title')}</h1>
          <Typography className="text-gray-500 dark:text-neutral-400">{t('profile.security.subtitle')}</Typography>
        </div>

        {/* Email Section */}
        <section className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-neutral-200 border-l-4 border-blue-500 pl-3">{t('profile.security.email.title')}</h2>
          <div className="grid grid-cols-1 gap-4">
            <div className="p-6 rounded-2xl border border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-800/40 shadow-inner group">
              <Typography className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-500 mb-2">{t('profile.security.email.current')}</Typography>
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                  <span className="font-bold">@</span>
                </div>
                <Typography className="text-xl font-semibold text-gray-700 dark:text-neutral-200">{currentEmail}</Typography>
              </div>
            </div>

            <div className="space-y-4">
              {emailStep === 'request' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
                    <TextField
                        label={t('profile.security.email.newEmail')}
                        value={newEmail}
                        onChange={e => setNewEmail(e.target.value)}
                        fullWidth
                        variant="outlined"
                        size="small"
                        className="dark:bg-neutral-800/50"
                    />
                    <Button
                        onClick={handleRequestEmailChange}
                        disabled={!newEmail.trim()}
                        variant="contained"
                        className="w-full sm:w-fit px-6 h-[40px] rounded-lg font-bold bg-dark-primary whitespace-nowrap flex-shrink-0"
                        disableElevation
                    >
                      {t('profile.security.email.request')}
                    </Button>
                  </div>
              ) : (
                  <div className="p-5 rounded-2xl border border-blue-100 dark:border-blue-900 bg-blue-50/30 dark:bg-blue-900/10 space-y-4 animate-in fade-in zoom-in-95 duration-300">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                        <Typography className="text-sm font-bold text-blue-600 dark:text-blue-400">{t('profile.security.email.timer')}: {Math.floor(emailTimer / 60)}:{('0' + (emailTimer % 60)).slice(-2)}</Typography>
                      </div>
                      <Button size="small" onClick={() => { setEmailStep('request'); setEmailCode(''); }} className="text-gray-400 hover:text-red-500">{t('profile.security.common.cancel')}</Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 items-start">
                      <TextField
                          label={t('profile.security.email.code')}
                          value={emailCode}
                          onChange={e => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          fullWidth
                          variant="outlined"
                          size="small"
                          className="bg-white dark:bg-neutral-800"
                          inputProps={{ className: 'tracking-[0.3em] font-mono' }}
                      />
                      <Button
                          onClick={handleConfirmEmailChange}
                          disabled={!emailCodeValid}
                          variant="contained"
                          className="w-full sm:w-fit px-6 h-[40px] rounded-lg font-bold bg-dark-primary whitespace-nowrap flex-shrink-0"
                          disableElevation
                      >
                        {t('profile.security.email.confirm')}
                      </Button>
                    </div>
                  </div>
              )}
            </div>
          </div>
        </section>

        <Divider className="dark:border-neutral-800" />

        {/* MFA Section */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-neutral-200 border-l-4 border-emerald-500 pl-3">{t('profile.security.mfa.title')}</h2>
            <div className={`flex items-center space-x-2 px-4 py-1.5 rounded-full border ${mfaEnabled ? 'bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400' : 'bg-gray-50 border-gray-100 text-gray-500 dark:bg-neutral-800 dark:border-neutral-700'}`}>
              <div className={`w-2 h-2 rounded-full ${mfaEnabled ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]' : 'bg-gray-400'}`} />
              <span className="text-xs font-black uppercase tracking-widest">{mfaEnabled ? t('profile.security.mfa.enabled') : t('profile.security.mfa.disabled')}</span>
            </div>
          </div>
          <div className="pt-2">
            {mfaEnabled ? (
                <Button variant="contained" color="error" onClick={() => setModalMode('remove-mfa')} className="rounded-xl font-bold px-6">{t('profile.security.mfa.removeButton')}</Button>
            ) : (
                <Button variant="contained" onClick={() => setModalMode('add-mfa')} disableElevation className="rounded-xl h-11 px-12 font-bold active:scale-95 transition-all bg-dark-primary">{t('profile.security.mfa.setupButton')}</Button>
            )}
          </div>
        </section>

        <Divider className="dark:border-neutral-800" />

        {/* Account Deletion Section */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-neutral-200 border-l-4 border-red-500 pl-3">{t('profile.security.delete.title')}</h2>
          <div className="p-6 rounded-2xl border border-red-100 dark:border-red-900/30 bg-red-50/30 dark:bg-red-900/10">
            <Typography className="text-gray-600 dark:text-neutral-400 mb-4">{t('profile.security.delete.description')}</Typography>
            <Button
                variant="outlined"
                color="error"
                onClick={() => setDeleteModalOpen(true)}
                className="rounded-xl font-bold px-6 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-900/20"
            >
              {t('profile.security.delete.button')}
            </Button>
          </div>
        </section>

        <SecurityActionModal
            open={modalMode !== null}
            mode={modalMode}
            onClose={() => setModalMode(null)}
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            mfaSetupData={mfaSetupData}
            mfaCode={mfaCode}
            setMfaCode={setMfaCode}
            mfaCodeValid={mfaCodeValid}
            verifyingMfa={verifyingMfa}
            onSubmit={handleMfaSubmit}
            onBack={() => setActiveStep(0)}
        />

        <AccountDeleteModal
            open={deleteModalOpen}
            userId={userId}
            onClose={() => setDeleteModalOpen(false)}
            onDeleted={handleAccountDeleted}
        />
      </Paper>
  );
};

export default Security;